import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 819 (Chad) — the accountant's subscription charges: each initial + recurring charge is a
// reconciliation record that must be ENTERED into QuickBooks and DEPOSITED (bank-verified), even
// when Stripe auto-paid it.
export interface SubscriptionChargeLine {
  sku?: string;
  brand?: string;
  description?: string;
  amount: number;
}

export interface SubscriptionChargeRow {
  id: string;
  recurringRevenueId: string;
  stripeSubscriptionId?: string | null; // item 1591 — the parent sub's Stripe id (ledger unit tag)
  recurringName?: string;
  customerName?: string;
  projectName?: string;
  stripeInvoiceNumber?: string;
  stripeInvoiceId?: string;
  amount: number;
  issuedDate: string;
  linesJson?: string;
  memoText?: string;
  customerPoNumber?: string;
  isEntered: boolean;
  enteredByName?: string | null;
  enteredDate?: string | null;
  isDeposited: boolean;
  depositedByName?: string | null;
  depositedDate?: string | null;
  depositMethod?: string | null;
  depositReference?: string | null;
  stripePaid: boolean;
  stripePaidDate?: string | null;
  stripeProcessingDate?: string | null; // item 1593 — a bank debit is clearing
  // item 1047 — genuine Stripe money (false) vs a mirrored out-of-band settle (true)
  stripePaidOutOfBand?: boolean;
  // item 1047 — the manual money event (check/wire arrived; Stripe untouched until deposit)
  paidDate?: string | null;
  // item 941 — enrollment status (0 Active / 1 Paused / 2 Cancelled) + billing cadence
  // (0 Monthly / 1 Quarterly / 2 Annual / 3 Custom) from the parent subscription
  subscriptionStatus?: number;
  frequency?: number;
  customIntervalDays?: number | null;
}

// item 941 — shared labels for the accountant ledgers
export const SUBSCRIPTION_STATUS_LABEL: Record<number, string> = {
  0: 'ENROLLED', 1: 'PAUSED', 2: 'CANCELED',
};
export const frequencyLabel = (frequency?: number, customDays?: number | null): string => {
  if (frequency === 0) return 'Monthly';
  if (frequency === 1) return 'Quarterly';
  if (frequency === 2) return 'Annually';
  if (frequency === 3) return customDays ? `Every ${customDays} days` : 'Custom';
  return '—';
};

const useSubscriptionCharges = () => {
  const qc = useQueryClient();
  const query = useQuery<SubscriptionChargeRow[]>('subscriptionCharges', async () => {
    const { data } = await axios.get(`${apiUrl()}SubscriptionCharge`);
    return data;
  });

  const invalidate = () => qc.invalidateQueries('subscriptionCharges');

  const markEntered = useMutation(
    (ids: string[]) => axios.post(`${apiUrl()}SubscriptionCharge/MarkEntered`, { ids }),
    { onSuccess: invalidate },
  );
  const markDeposited = useMutation(
    (v: { ids: string[]; method: string; reference: string }) => axios.post(`${apiUrl()}SubscriptionCharge/MarkDeposited`, v),
    { onSuccess: invalidate },
  );

  return {
    charges: query.data ?? [],
    status: query.status,
    markEntered: (ids: string[]) => markEntered.mutateAsync(ids),
    markDeposited: (ids: string[], method: string, reference: string) => markDeposited.mutateAsync({ ids, method, reference }),
    invalidate,
  };
};

export default useSubscriptionCharges;
